import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { server } from "../server";
import Header from "../components/Layout/Header";
import Footer from "../components/Layout/Footer";
import { getFeaturedProducts, getBestSellers, getAllProducts } from "../redux/actions/product";
import { getAllCategoriesPublic } from "../redux/actions/category";
import {
  HiOutlineSparkles,
  HiOutlineShieldCheck,
  HiOutlineTruck,
  HiOutlineHeart,
  HiOutlineStar,
  HiOutlineArrowRight,
  HiOutlineGift,
  HiOutlineBeaker,
  HiOutlineThumbUp,
} from "react-icons/hi";

/* ─────────────────────────────────────────
   PRODUCT CARD
───────────────────────────────────────── */
const ProductCard = ({ product }) => {
  const effectivePrice = product.discountedPrice || product.price;
  const hasDiscount = product.discountedPrice && product.discountedPrice < product.price;
  const discountPercent = hasDiscount
    ? Math.round(((product.price - product.discountedPrice) / product.price) * 100)
    : 0;
  const imageUrl =
    product.images && product.images.length > 0
      ? product.images[0]?.url || product.images[0]
      : null;

  return (
    <Link
      to={`/product/${product.slug || product._id}`}
      className="group bg-white border border-secondary-200 hover:border-black overflow-hidden transition-all duration-200 hover:shadow-moc-md"
    >
      <div className="relative aspect-square overflow-hidden bg-secondary-50">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-secondary-100">
            <HiOutlineSparkles className="w-10 h-10 text-secondary-300" />
          </div>
        )}
        {hasDiscount && (
          <span className="absolute top-2 left-2 bg-accent-500 text-black text-[11px] font-bold px-2 py-0.5">
            -{discountPercent}%
          </span>
        )}
        {product.isBestSeller && !hasDiscount && (
          <span className="absolute top-2 right-2 bg-black text-white text-[11px] font-medium px-2 py-0.5 uppercase tracking-wider">
            Best Seller
          </span>
        )}
      </div>
      <div className="p-4">
        {product.productType && (
          <p className="text-[10px] font-medium text-accent-600 uppercase tracking-widest mb-1">
            {product.productType}
          </p>
        )}
        <h3 className="font-medium text-gray-900 text-sm leading-snug line-clamp-2 group-hover:text-black transition-colors">
          {product.name}
        </h3>
        <div className="flex items-center gap-2 mt-2">
          <span className="font-bold text-black">{effectivePrice?.toLocaleString("en-IN")}</span>
          {hasDiscount && (
            <span className="text-secondary-400 line-through text-sm">
              {product.price?.toLocaleString("en-IN")}
            </span>
          )}
        </div>
        {product.ratings > 0 && (
          <div className="flex items-center gap-1 mt-1.5">
            {[...Array(5)].map((_, i) => (
              <HiOutlineStar
                key={i}
                className={`w-3 h-3 ${
                  i < Math.round(product.ratings)
                    ? "text-accent-500 fill-current"
                    : "text-secondary-200"
                }`}
              />
            ))}
            <span className="text-[11px] text-secondary-400 ml-0.5">
              ({product.reviews?.length || 0})
            </span>
          </div>
        )}
      </div>
    </Link>
  );
};

/* ─────────────────────────────────────────
   SECTION HEADER
───────────────────────────────────────── */
const SectionHeader = ({ eyebrow, title, subtitle, light = false, center = true }) => (
  <div className={center ? "text-center mb-10" : "mb-10"}>
    {eyebrow && (
      <p className={`text-xs font-semibold uppercase tracking-[0.2em] mb-2 ${light ? "text-accent-400" : "text-accent-600"}`}>
        {eyebrow}
      </p>
    )}
    <h2 className={`text-3xl md:text-4xl font-bold leading-tight ${light ? "text-white" : "text-black"}`}>
      {title}
    </h2>
    {subtitle && (
      <p className={`mt-3 text-base max-w-xl ${center ? "mx-auto" : ""} ${light ? "text-secondary-300" : "text-secondary-500"}`}>
        {subtitle}
      </p>
    )}
    <div className={`mt-5 w-16 h-0.5 bg-accent-500 ${center ? "mx-auto" : ""}`} />
  </div>
);

/* ─────────────────────────────────────────
   NEWSLETTER
───────────────────────────────────────── */
const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${server}/newsletter/subscribe`, { email });
      if (data.success) {
        setSubscribed(true);
        setEmail("");
        setTimeout(() => setSubscribed(false), 4000);
      }
    } catch (err) {
      console.error("Newsletter error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-black py-16">
      <div className="max-w-2xl mx-auto px-4 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-accent-500 mb-3">Stay Connected</p>
        <h2 className="text-3xl font-bold text-white mb-3">
          Join the Positive Moments Family
        </h2>
        <p className="text-secondary-400 mb-8 text-sm">
          Exclusive offers, wellness tips, and new arrivals — straight to your inbox.
        </p>
        {subscribed ? (
          <div className="inline-flex items-center gap-2 border border-accent-500 text-accent-400 px-6 py-3 text-sm">
            <HiOutlineShieldCheck className="w-4 h-4" />
            Welcome to the family!
          </div>
        ) : (
          <form onSubmit={handleSubscribe} className="flex gap-0 max-w-sm mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              className="flex-1 px-4 py-3 bg-secondary-900 border border-secondary-700 text-white placeholder-secondary-500 text-sm focus:outline-none focus:border-accent-500"
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-accent-500 text-black font-semibold text-sm hover:bg-accent-400 transition-colors disabled:opacity-60 uppercase tracking-wider"
            >
              {loading ? "..." : "Subscribe"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────
   HOME PAGE
───────────────────────────────────────── */
const HomePage = () => {
  const dispatch = useDispatch();
  const { featuredProducts, bestSellers, allProducts } = useSelector((state) => state.products);
  const { categories } = useSelector((state) => state.categories);
  const [blogs, setBlogs] = useState([]);
  const [testimonials] = useState([
    {
      name: "Priya Sharma",
      location: "Kerala",
      text: "The Kumkumadi serum completely transformed my skin in just 3 weeks. Pure, natural results — exactly what my skin needed.",
      rating: 5,
    },
    {
      name: "Ankit Verma",
      location: "Rajasthan",
      text: "The Ayurvedic Wellness Kit was a perfect gift. Beautiful packaging and genuinely effective products inside.",
      rating: 5,
    },
    {
      name: "Meera Nair",
      location: "Tamil Nadu",
      text: "I switched completely to Positive Moments for my daily wellness routine. Ashwagandha capsules have made a real difference.",
      rating: 5,
    },
  ]);

  useEffect(() => {
    dispatch(getFeaturedProducts());
    dispatch(getBestSellers());
    dispatch(getAllProducts({ limit: 500 }));
    dispatch(getAllCategoriesPublic());
    fetchBlogs();
  }, [dispatch]);

  const fetchBlogs = async () => {
    try {
      const { data } = await axios.get(`${server}/blog/get-all-blogs?limit=3`);
      if (data.success) setBlogs(data.blogs || []);
    } catch (err) {
      // silent
    }
  };

  const displayFeatured =
    featuredProducts?.length > 0
      ? featuredProducts.slice(0, 8)
      : allProducts?.filter((p) => p.isFeatured)?.slice(0, 8) || allProducts?.slice(0, 8) || [];

  const displayBestSellers =
    bestSellers?.length > 0
      ? bestSellers.slice(0, 8)
      : allProducts?.filter((p) => p.isBestSeller)?.slice(0, 8) || [];

  const newArrivals = allProducts?.slice(0, 4) || [];

  return (
    <div className="bg-white min-h-screen">
      <Header activeHeading={1} />

      {/* ══════════════════════════════════════
          HERO
      ══════════════════════════════════════ */}
      <section className="relative bg-black overflow-hidden">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1490818387583-1baba5e638af?w=1600&q=60')", backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 py-28 md:py-36 lg:py-48">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.3em] text-accent-500 mb-6">
              Est. 2024 &nbsp;·&nbsp; 100% Natural
            </p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-8">
              Nature's finest.<br />
              <span className="text-accent-500">Delivered.</span>
            </h1>
            <p className="text-base md:text-lg text-secondary-300 max-w-xl mb-10 leading-relaxed">
              Premium Ayurvedic &amp; handcrafted wellness products, sourced from across India's most renowned regions.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 text-black font-semibold hover:bg-accent-400 transition-colors uppercase tracking-wider text-sm"
              >
                Shop Collection
                <HiOutlineArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-8 py-3.5 border border-white/30 text-white font-medium hover:bg-white/10 transition-colors uppercase tracking-wider text-sm"
              >
                Our Story
              </Link>
            </div>
            {/* Trust marks */}
            <div className="flex items-center gap-8 mt-12 flex-wrap">
              {[
                { icon: HiOutlineShieldCheck, label: "100% Natural" },
                { icon: HiOutlineTruck, label: "Free Shipping ₹499+" },
                { icon: HiOutlineHeart, label: "Cruelty Free" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-secondary-400 text-sm">
                  <item.icon className="w-4 h-4 text-accent-500" />
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          BRAND PROMISE STRIP
      ══════════════════════════════════════ */}
      <section className="border-b border-secondary-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-secondary-100">
            {[
              { icon: HiOutlineBeaker, title: "Organic Sourcing", desc: "Traceable farm-to-door" },
              { icon: HiOutlineTruck, title: "Fast Delivery", desc: "Pan-India shipping" },
              { icon: HiOutlineShieldCheck, title: "Lab Certified", desc: "Quality tested" },
              { icon: HiOutlineGift, title: "Gift Ready", desc: "Beautiful packaging" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 py-6 px-6">
                <item.icon className="w-7 h-7 text-accent-500 flex-shrink-0" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-black">{item.title}</p>
                  <p className="text-xs text-secondary-400 mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          SHOP HIGHLIGHTS  (2-column editorial boxes)
      ══════════════════════════════════════ */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <SectionHeader eyebrow="Explore" title="Discover Our World" center={false} />
        <div className="grid md:grid-cols-2 gap-6">
          <Link
            to="/products?type=Herbal"
            className="group relative overflow-hidden bg-black aspect-[4/3] flex items-end"
          >
            <img
              src="https://images.unsplash.com/photo-1515694346937-94d85e41e6f0?w=900&q=70"
              alt="Herbal Collection"
              className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-50 group-hover:scale-105 transition-all duration-500"
            />
            <div className="relative z-10 p-8">
              <p className="text-xs text-accent-400 uppercase tracking-widest mb-2">New Collection</p>
              <h3 className="text-3xl font-bold text-white">Herbal Range</h3>
              <p className="text-secondary-300 text-sm mt-2 mb-4">Teas, oils &amp; adaptogens crafted from nature</p>
              <span className="inline-flex items-center gap-1 text-accent-400 text-sm font-medium group-hover:gap-3 transition-all">
                Shop Now <HiOutlineArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>

          <Link
            to="/products?type=Handmade"
            className="group relative overflow-hidden bg-black aspect-[4/3] flex items-end"
          >
            <img
              src="https://images.unsplash.com/photo-1609445478720-0d96b45e4414?w=900&q=70"
              alt="Handmade Collection"
              className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-50 group-hover:scale-105 transition-all duration-500"
            />
            <div className="relative z-10 p-8">
              <p className="text-xs text-accent-400 uppercase tracking-widest mb-2">Artisan Made</p>
              <h3 className="text-3xl font-bold text-white">Handmade</h3>
              <p className="text-secondary-300 text-sm mt-2 mb-4">Soaps, skincare &amp; wellness kits  crafted with care</p>
              <span className="inline-flex items-center gap-1 text-accent-400 text-sm font-medium group-hover:gap-3 transition-all">
                Shop Now <HiOutlineArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FEATURED PRODUCTS
      ══════════════════════════════════════ */}
      {displayFeatured.length > 0 && (
        <section className="bg-secondary-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-end justify-between mb-10">
              <SectionHeader
                eyebrow="Curated for You"
                title="Featured Products"
                center={false}
              />
              <Link
                to="/products"
                className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-black hover:text-accent-600 transition-colors uppercase tracking-wider border-b border-black pb-0.5 mb-12"
              >
                View All <HiOutlineArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {displayFeatured.map((product) => (
                <ProductCard key={product._id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════
          ABOUT / VALUES  (black section)
      ══════════════════════════════════════ */}
      <section className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-accent-500 mb-4">About Us</p>
              <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
                Our Happy Place
              </h2>
              <p className="text-secondary-300 leading-relaxed mb-6">
                Positive Moments was born from a simple belief — that nature holds the answer to most of what we need. We source the finest herbs, botanicals, and handcrafted goods from India's most celebrated regions and bring them to your doorstep.
              </p>
              <p className="text-secondary-400 leading-relaxed mb-8 text-sm">
                From Himalayan Shilajit to Kerala's Kumkumadi, every product is carefully selected, quality-tested, and packed with love.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-3 border border-accent-500 text-accent-500 text-sm font-medium hover:bg-accent-500 hover:text-black transition-all uppercase tracking-wider"
              >
                Our Story <HiOutlineArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: HiOutlineBeaker, title: "100% Organic", desc: "Ethically sourced from certified organic farms across India" },
                { icon: HiOutlineShieldCheck, title: "Lab Tested", desc: "Every batch undergoes quality and purity testing" },
                { icon: HiOutlineHeart, title: "Cruelty Free", desc: "No animal testing, ever. Certified compassionate" },
                { icon: HiOutlineThumbUp, title: "5000+ Happy Customers", desc: "Growing community of wellness enthusiasts" },
              ].map((item, i) => (
                <div key={i} className="border border-secondary-800 p-5 hover:border-accent-500 transition-colors">
                  <item.icon className="w-6 h-6 text-accent-500 mb-3" />
                  <h4 className="text-white font-semibold text-sm mb-1">{item.title}</h4>
                  <p className="text-secondary-500 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          CATEGORIES
      ══════════════════════════════════════ */}
      {categories && categories.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <SectionHeader
              eyebrow="Browse By"
              title="Shop By Category"
              subtitle="Discover our range of Ayurvedic, herbal, and handcrafted wellness products"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.slice(0, 6).map((cat) => (
                <Link
                  key={cat._id}
                  to={`/products?category=${cat._id}`}
                  className="group border border-secondary-200 p-5 text-center hover:border-black transition-colors"
                >
                  {cat.image?.url ? (
                    <img src={cat.image.url} alt={cat.name} className="w-14 h-14 mx-auto mb-3 object-cover" />
                  ) : (
                    <div className="w-14 h-14 mx-auto mb-3 bg-secondary-100 flex items-center justify-center">
                      <HiOutlineSparkles className="w-7 h-7 text-secondary-400 group-hover:text-accent-500 transition-colors" />
                    </div>
                  )}
                  <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wider group-hover:text-black transition-colors">
                    {cat.name}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════
          BEST SELLERS
      ══════════════════════════════════════ */}
      {displayBestSellers.length > 0 && (
        <section className="bg-secondary-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-end justify-between mb-10">
              <SectionHeader
                eyebrow="Most Loved"
                title="Best Sellers"
                center={false}
              />
              <Link
                to="/best-selling"
                className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-black hover:text-accent-600 transition-colors uppercase tracking-wider border-b border-black pb-0.5 mb-12"
              >
                View All <HiOutlineArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {displayBestSellers.map((product) => (
                <ProductCard key={product._id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════
          WELLNESS KITS / GIFT  (editorial full-width banner)
      ══════════════════════════════════════ */}
      <section className="relative overflow-hidden bg-black py-24">
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1607006346965-6fe1c07b0471?w=1400&q=60')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-accent-500 mb-4">Thoughtful Gifting</p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Wellness Gift Kits
          </h2>
          <p className="text-secondary-300 max-w-xl mx-auto text-base mb-10">
            Beautifully packaged wellness kits — the perfect gift for someone you love. Each kit is curated with our bestselling products for a holistic experience.
          </p>
          <Link
            to="/products?category=wellness-kits"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 text-black font-semibold hover:bg-accent-400 transition-colors uppercase tracking-wider text-sm"
          >
            Shop Gift Kits <HiOutlineGift className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ══════════════════════════════════════
          TESTIMONIALS
      ══════════════════════════════════════ */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeader
            eyebrow="What People Say"
            title="Customer Stories"
            subtitle="Real stories from our community of wellness enthusiasts"
          />
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((item, i) => (
              <div key={i} className="border border-secondary-200 p-8 hover:border-secondary-400 transition-colors">
                <div className="flex gap-0.5 mb-5">
                  {[...Array(item.rating)].map((_, j) => (
                    <HiOutlineStar key={j} className="w-4 h-4 text-accent-500 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 text-sm italic">
                  "{item.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-black flex items-center justify-center text-white font-bold text-sm">
                    {item.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{item.name}</p>
                    <p className="text-secondary-400 text-xs">{item.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          BLOG
      ══════════════════════════════════════ */}
      {blogs.length > 0 && (
        <section className="bg-secondary-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-end justify-between mb-10">
              <SectionHeader eyebrow="From Our Journal" title="Latest Articles" center={false} />
              <Link
                to="/blog"
                className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-black hover:text-accent-600 uppercase tracking-wider border-b border-black pb-0.5 mb-12 transition-colors"
              >
                Read More <HiOutlineArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {blogs.map((blog) => (
                <Link
                  key={blog._id}
                  to={`/blog/${blog.slug || blog._id}`}
                  className="group bg-white border border-secondary-200 hover:border-black overflow-hidden transition-colors"
                >
                  {blog.coverImage?.url && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={blog.coverImage.url}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="font-bold text-gray-900 group-hover:text-black line-clamp-2 mb-2 transition-colors">
                      {blog.title}
                    </h3>
                    <p className="text-secondary-400 text-sm line-clamp-2 mb-4">
                      {blog.excerpt || blog.summary}
                    </p>
                    <span className="inline-flex items-center gap-1 text-black text-xs font-semibold uppercase tracking-wider border-b border-black pb-0.5">
                      Read Article <HiOutlineArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════
          NEWSLETTER
      ══════════════════════════════════════ */}
      <NewsletterSection />

      {/* ══════════════════════════════════════
          INSTAGRAM / SOCIAL
      ══════════════════════════════════════ */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-accent-600 mb-2">Social</p>
          <h2 className="text-2xl font-bold text-black mb-2">@positivemoments</h2>
          <p className="text-secondary-400 text-sm mb-8">Tag us and share your wellness journey</p>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {[
              "https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=400&h=400&fit=crop",
              "https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?w=400&h=400&fit=crop",
              "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=400&h=400&fit=crop",
              "https://images.unsplash.com/photo-1607006346965-6fe1c07b0471?w=400&h=400&fit=crop",
              "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=400&fit=crop",
              "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400&h=400&fit=crop",
            ].map((url, i) => (
              <div key={i} className="aspect-square overflow-hidden group cursor-pointer">
                <img
                  src={url}
                  alt={`Social post ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 group-hover:opacity-80"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
